import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { __, redirect, setTableParams } from '../../core/utils';
import {
  createSidebar,
  createSuperadminSidebar,
} from '../../redux/actions/general/sidebar';
import { listActiveModules } from '../../redux/actions/general/active_modules';
import { setNotification } from '../../redux/actions/general/notification';
import {
  hideAlertbox,
  showAlertbox,
} from '../../redux/actions/general/alertbox';
import {
  deletePlanningLibraryItem,
  listPlanningLibraryItems,
  listPlanningTemplates,
  reorderPlanningLibraryItems,
  updatePlanningLibraryItem,
} from '../../redux/actions/table/planning_library';
import TitleBar from '../../containers/TitleBar';
import Button from '../../components/Button';
import PlanningLibraryTable from './PlanningTemplates/PlanningLibraryTable';
import PlanningTemplateBuilder from './PlanningTemplates/PlanningTemplateBuilder';
import { createEmptyPlanningTemplate } from './PlanningTemplates/helpers';
import styles from '../../styles/views/company/PlanningTemplates.scss';
import { setPlusActions } from '../../redux/actions/general/plus';

const SECTIONS = {
  stages: {
    type: 'stage',
    titleKey: 'planning-templates.tabs.stages',
    title: 'Stages',
    descriptionKey: 'planning-templates.stages.description',
    description:
      'Library of reusable stages available when building planning templates.',
  },
  substages: {
    type: 'substage',
    titleKey: 'planning-templates.tabs.substages',
    title: 'Substages',
    descriptionKey: 'planning-templates.substages.description',
    description:
      'Library of reusable substages available under any stage in a template.',
  },
  groups: {
    type: 'group',
    titleKey: 'planning-templates.tabs.groups',
    title: 'Groups',
    descriptionKey: 'planning-templates.groups.description',
    description: 'Library of reusable task groups available under substages.',
  },
  templates: {
    type: null,
    titleKey: 'planning-templates.tabs.templates',
    title: 'Templates',
    descriptionKey: 'planning-templates.templates.description',
    description:
      'Build reusable project planning structures from stages, substages and groups.',
  },
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  table: state.table.planning_library,
});

@connect(mapStateToProps)
class PlanningTemplates extends Component {
  constructor(props) {
    super(props);

    this.activeUpdateRequests = {};
  }

  componentDidMount() {
    listActiveModules();
    this.initializeManagementScope();

    if (this.props.isSystem) {
      createSuperadminSidebar();
    } else {
      createSidebar({
        title: __('topbar.account.company-settings'),
        items: [
          {
            title: __('submenu.title.company-details'),
            link: () => '/company/details',
          },
          {
            title: __('submenu.title.offices'),
            link: () => '/company/offices',
          },
          {
            title: __('company-settings.submenu.title.brands'),
            link: () => '/company/brands',
          },
          {
            title: __('company-settings.submenu.title.disciplines'),
            link: () => '/company/disciplines',
          },
          {
            title: __('submenu.title.market'),
            link: () => '/company/market',
          },
          {
            title: __('submenu.title.company_holidays'),
            link: () => '/company/company_holidays',
          },
          {
            title: __('submenu.title.company_positions'),
            link: () => '/company/company_positions',
          },
          {
            title: __('submenu.title.company_segments'),
            link: () => '/company/company_segments',
          },
          {
            title: __('submenu.title.company_benefits'),
            link: () => '/company/company_benefits',
          },
          {
            title: __('submenu.title.company_folders'),
            link: () => '/company/company_folders',
          },
          {
            title: __('submenu.title.planning_templates'),
            link: () => '/company/planning-templates/stages',
            active: true,
          },
          {
            title: __('submenu.title.tags'),
            link: () => '/company/tags',
          },
          _.includes(
            this.props.auth?.permissions,
            'view.permissions.in.permission.manager',
          )
            ? {
                title: __('submenu.title.company_roles_and_permissions'),
                link: () => '/company/company_roles_and_permissions',
              }
            : null,
        ].filter(Boolean),
      });
    }

    this.updatePlusActions();
    this.loadItems();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isSystem !== this.props.isSystem) {
      this.initializeManagementScope();
      this.loadItems();
    }

    if (prevProps.match.params.section !== this.props.match.params.section) {
      setTableParams('planning_library', { editor: undefined });
      this.loadItems();
    }

    this.updatePlusActions();
  }

  updatePlusActions() {
    const activeSection = this.getActiveSection();
    const section = SECTIONS[activeSection];

    if (!section.type || !this.canAddPlanningTemplate()) {
      return setPlusActions([]);
    }

    setPlusActions([
      {
        title: `${__(section.titleKey, {}, section.title).replace(/s$/, '')}`,
        onClick: () =>
          section.type ? this.openEditor() : this.createNewTemplate(),
      },
    ]);
  }

  getActiveSection() {
    const section = this.props.match.params.section;

    return SECTIONS[section] ? section : 'stages';
  }

  initializeManagementScope() {
    const managementScope = this.props.isSystem ? 'system' : 'company';

    if (this.props.table.management_scope === managementScope) {
      return;
    }

    setTableParams('planning_library', {
      management_scope: managementScope,
      data: [],
      templates: [],
      editor: undefined,
      template_draft: undefined,
      template_dirty: false,
      template_editor: undefined,
      template_chooser: undefined,
    });
  }

  getBasePath() {
    return this.props.isSystem
      ? '/superadmin/planning-templates'
      : '/company/planning-templates';
  }

  canAddPlanningTemplate() {
    return (
      !this.props.isSystem ||
      Boolean(this.props.auth.superadmin?.add_planning_template)
    );
  }

  canEditSystemPlanningTemplate() {
    return Boolean(
      this.props.isSystem && this.props.auth.superadmin?.edit_planning_template,
    );
  }

  canDeleteSystemPlanningTemplate() {
    return Boolean(
      this.props.isSystem &&
      this.props.auth.superadmin?.delete_planning_template,
    );
  }

  loadItems() {
    const section = SECTIONS[this.getActiveSection()];

    if (!section.type) {
      setTableParams('planning_library', { data: [], loading: false });
      listPlanningTemplates(this.props.isSystem);
      return;
    }

    listPlanningLibraryItems(section.type, this.props.isSystem);
  }

  createNewTemplate() {
    if (!this.canAddPlanningTemplate()) {
      return;
    }

    setTableParams('planning_library', {
      template_draft: createEmptyPlanningTemplate(this.props.auth.language_id),
      template_dirty: false,
    });
  }

  openEditor(item = null) {
    if (
      this.props.isSystem &&
      (item
        ? !this.canEditSystemPlanningTemplate()
        : !this.canAddPlanningTemplate())
    ) {
      return;
    }

    const section = SECTIONS[this.getActiveSection()];
    const languageId = this.props.auth.language_id;

    setTableParams('planning_library', {
      editor: {
        id: item?.id,
        initialValues: item
          ? {
              name: item.name,
              code: item.code,
              description: item.description,
              active: item.active ? 1 : 0,
            }
          : {
              name: { [languageId]: '' },
              code: '',
              description: {},
              active: 1,
            },
        type: section.type,
        isSystem: this.props.isSystem,
      },
    });
  }

  deleteItem(item) {
    if (this.props.isSystem && !this.canDeleteSystemPlanningTemplate()) {
      return;
    }

    const description = this.props.isSystem
      ? __('planning-templates.system-delete.description')
      : __('planning-templates.delete.description');

    showAlertbox({
      color: 'red',
      title: __('general.alert.are-you-sure'),
      description,
      buttons: [
        {
          color: 'lightGray',
          text: __('general.alert.no-close'),
          onClick: 'close',
        },
        {
          color: 'gray',
          text: __('general.alert.yes-delete'),
          onClick: () => {
            deletePlanningLibraryItem(item.id, this.props.isSystem)
              .then(() => {
                hideAlertbox();
                this.loadItems();
                setNotification(__('planning-templates.notification.deleted'));
              })
              .catch(({ response }) => {
                hideAlertbox();
                setNotification({
                  text:
                    response?.message ||
                    __('planning-templates.notification.delete-failed'),
                  type: 'warning',
                });
              });
          },
        },
      ],
    });
  }

  reorderItems(items) {
    const type = SECTIONS[this.getActiveSection()].type;

    setTableParams('planning_library', { data: items });

    reorderPlanningLibraryItems(type, _.map(items, 'id')).catch(() => {
      this.loadItems();
    });
  }

  toggleActive(item, active) {
    if (item.is_system && !this.canEditSystemPlanningTemplate()) {
      return;
    }

    const previousActive = item.active;

    setTableParams('planning_library', {
      data: _.map(this.props.table.data, (libraryItem) =>
        libraryItem.id === item.id ? { ...libraryItem, active } : libraryItem,
      ),
    });

    const previousRequest =
      this.activeUpdateRequests[item.id] || Promise.resolve();
    const request = previousRequest
      .catch(() => undefined)
      .then(() =>
        updatePlanningLibraryItem(
          item.id,
          {
            type: item.type,
            code: item.code,
            name: item.name,
            description: item.description,
            active,
          },
          this.props.isSystem,
        ),
      );

    this.activeUpdateRequests[item.id] = request;

    request.catch(({ response }) => {
      const currentItem = _.find(this.props.table.data, { id: item.id });

      if (currentItem?.active === active) {
        setTableParams('planning_library', {
          data: _.map(this.props.table.data, (libraryItem) =>
            libraryItem.id === item.id
              ? { ...libraryItem, active: previousActive }
              : libraryItem,
          ),
        });
      }

      setNotification({
        text:
          response?.message ||
          __('planning-templates.notification.update-failed'),
        type: 'warning',
      });
    });

    request.then(
      () => {
        if (this.activeUpdateRequests[item.id] === request) {
          delete this.activeUpdateRequests[item.id];
        }
      },
      () => {
        if (this.activeUpdateRequests[item.id] === request) {
          delete this.activeUpdateRequests[item.id];
        }
      },
    );
  }

  render() {
    const activeSection = this.getActiveSection();
    const section = SECTIONS[activeSection];
    const sectionTitle = __(section.titleKey);

    return (
      <div className={styles.wrapper}>
        <TitleBar
          toolbar
          title={__('planning-templates.title')}
          dropdownHeader={
            this.props.isSystem
              ? __('modules.title.superadmin')
              : __('topbar.account.company-settings')
          }
          loading={
            this.props.table.loading || this.props.table.templates_loading
          }
        />

        <nav className={styles.tabs}>
          {_.map(SECTIONS, (item, key) => (
            <button
              type='button'
              key={key}
              className={classNames(
                styles.tab,
                key === activeSection && styles.activeTab,
              )}
              onClick={() => redirect(`${this.getBasePath()}/${key}`)}
            >
              {__(item.titleKey)}
            </button>
          ))}
        </nav>

        <section className={styles.content}>
          {section.type ? (
            <>
              <PlanningLibraryTable
                canDeleteSystem={this.canDeleteSystemPlanningTemplate()}
                canEditSystem={this.canEditSystemPlanningTemplate()}
                data={this.props.table.data}
                languageId={this.props.auth.language_id}
                itemTitle={sectionTitle.replace(/s$/, '')}
                emptyText={__('planning-templates.empty')}
                onEdit={(item) => this.openEditor(item)}
                onDelete={(item) => this.deleteItem(item)}
                onReorder={(items) => this.reorderItems(items)}
                reorderable={!this.props.isSystem}
                onToggleActive={(item, active) =>
                  this.toggleActive(item, active)
                }
              />
            </>
          ) : (
            <PlanningTemplateBuilder
              canAddTemplate={this.canAddPlanningTemplate()}
              canDeleteSystem={this.canDeleteSystemPlanningTemplate()}
              canEditSystem={this.canEditSystemPlanningTemplate()}
              isSystem={this.props.isSystem}
              languageId={this.props.auth.language_id}
              table={this.props.table}
            />
          )}
        </section>
      </div>
    );
  }
}

export default PlanningTemplates;
